import { z } from 'zod'
import { apiRequest } from './client'

/**
 * Client wrapper for `POST /api/v1/manager/peak-conflict/recommend`.
 *
 * Backs RFC 005 K2 / policy §3.4 — given two leave applications that
 * collide on the same peak window, the server returns a manager-facing
 * recommendation with score breakdowns, reasoning, fairness note,
 * watch-out flags, and a deterministic Slack-DM template for the
 * declined employee.
 *
 * **Contract (mirrors `services/api/src/schemas/conflict.py`):**
 * - `decision` is advisory only. The FE must NEVER auto-apply
 *   `winner` to the leave-decision endpoint — the manager clicks an
 *   explicit Approve A / Approve B / Open discussion / Decline both
 *   on the underlying request cards. The audit row on the actual
 *   approve/decide call carries `invocationId` linking back to this
 *   recommendation.
 * - `reasoning` / `fairnessNote` / `readyToSendMessage` are ILMU-generated
 *   (grounded in the deterministic scores) when narration is enabled, and
 *   fall back to deterministic templates otherwise — `isLlmGenerated`
 *   reflects which. The decision + scores are always deterministic.
 * - No verdict pill ("APPROVE · Aisyah") may render on this panel
 *   (memory `feedback_no_verdict_in_ai_analysis.md`). The score gap,
 *   routing, and per-factor breakdown are intentionally not surfaced to
 *   the manager — only the narrative.
 */

export const conflictScoreBreakdownSchema = z.object({
  religious: z.number().int(),
  married: z.number().int(),
  children: z.number().int(),
  distance: z.number().int(),
  // Policy §3.2/§3.3: tenure is awarded to the half-point (3 yrs → 4.5
  // points). `total` inherits the half-point. The other factors are
  // whole-integer bonuses.
  yearsOfService: z.number(),
  healthBarPenalty: z.number().int(),
  total: z.number(),
})
export type ConflictScoreBreakdown = z.infer<typeof conflictScoreBreakdownSchema>

export const conflictRecommendationSchema = z.object({
  decision: z.enum(['APPROVE_A', 'APPROVE_B', 'REJECT', 'DISCUSS']),
  winner: z.guid().nullable(),
  reasoning: z.string(),
  scoreA: conflictScoreBreakdownSchema,
  scoreB: conflictScoreBreakdownSchema,
  fairnessNote: z.string().nullable(),
  readyToSendMessage: z.string(),
  watchOutFlags: z.array(z.string()),
  advisoryOnly: z.boolean(),
  isLlmGenerated: z.boolean().default(false),
})
export type ConflictRecommendation = z.infer<typeof conflictRecommendationSchema>

export interface RecommendPeakConflictInput {
  leaveIdA: string
  leaveIdB: string
  /** Omitted for a plain schedule overlap with no peak window behind it. */
  peakPeriodId?: string
}

export async function recommendPeakConflict(
  input: RecommendPeakConflictInput,
): Promise<ConflictRecommendation> {
  const raw = await apiRequest<unknown>('/api/v1/manager/peak-conflict/recommend', {
    method: 'POST',
    body: input,
  })
  return conflictRecommendationSchema.parse(raw)
}
